import { useAtom } from 'jotai';
import { filterListStateAtom } from './filterList.atom';
import { filterListStore } from './FilterListAtomProvider';
import { useCallback, useMemo } from 'react';
import { useNavigation } from '@react-navigation/native';
import { useInstanceId } from 'shared/src/hooks';
import { useFilterSheetOverride } from '../FilterSheetOverrideContext';
import type { FilterListAtom } from './filterList.atom';
import { useFilterScope } from './FilterScopeContext';

/**
 * 값이 `undefined` 인 키를 실제로 제거한다.
 *
 * 해제(`{ [queryName]: undefined }`)를 merge 로만 처리하면 키가 남아, 요청 파라미터와
 * queryKey 직렬화에서 "해제됨"과 "그 필터가 없음"이 구분되지 않는다(JSON.stringify 가
 * undefined 값을 버리므로 해시도 같아진다).
 */
const omitUndefined = (value: FilterListAtom): FilterListAtom =>
  Object.fromEntries(
    Object.entries(value).filter(([, item]) => item !== undefined),
  );

export const useFilterListAtom = () => {
  const instanceId = useInstanceId();
  // atom 슬롯 키. 기본은 화면 단위(instanceId)이고, FilterScopeProvider 가 있으면 그 값으로
  // 더 좁힌다(예: 홈은 탭마다 격리). instanceId 자체는 네이티브 surface 식별자라 건드리지 않는다.
  const filterScope = useFilterScope();
  const scopeKey = filterScope ?? instanceId;
  /**
   * 스코프가 지정된 화면(예: 홈 탭)은 **atom 이 source of truth** 다.
   *
   * 그런 화면은 FilterListAtomProvider(params→atom replace)가 없는 대신 하나의 route 를
   * 여러 탭이 공유한다. 그래서 params 에 쓰면 탭 간에 필터가 섞인다.
   * 반대로 provider 가 있는 화면은 params 가 source of truth 라 params 에 쓴다
   * (atom 에 써도 곧 params 로 replace 되어 덮인다).
   */
  const isScopedFilter = filterScope != null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const navigation = useNavigation<any>();
  const override = useFilterSheetOverride();
  const setNavigationParams = useCallback(
    (params: Record<string, unknown>) => {
      if (override) {
        override.setParams(params);
      } else {
        navigation.setParams(params);
      }
    },
    [navigation, override],
  );
  /**
   * params 반영. 스코프가 지정된 화면은 atom 이 source of truth 이므로 params 를 쓰지 않는다
   * (route 를 공유하는 탭끼리 필터가 섞이는 것을 막는다).
   */
  const syncNavigationParams = useCallback(
    (params: Record<string, unknown>) => {
      if (isScopedFilter) return;
      setNavigationParams(params);
    },
    [isScopedFilter, setNavigationParams],
  );
  const [filterListAtomMap, setFilterListAtomMap] = useAtom(
    filterListStateAtom,
    { store: filterListStore },
  );

  // 현재 instanceId에 해당하는 상태 가져오기
  const filterListAtomValue = useMemo(
    () => filterListAtomMap[scopeKey] ?? {},
    [filterListAtomMap, scopeKey],
  );

  // 특정 instanceId의 상태만 업데이트하는 함수
  const setFilterListAtom = useCallback(
    (
      updater:
        | Partial<typeof filterListAtomValue>
        | ((
            prev: typeof filterListAtomValue,
          ) => Partial<typeof filterListAtomValue>),
    ) => {
      setFilterListAtomMap(prev => {
        const currentValue = prev[scopeKey] ?? {};
        const newValue =
          typeof updater === 'function' ? updater(currentValue) : updater;

        return {
          ...prev,
          [scopeKey]: omitUndefined({
            ...currentValue,
            ...newValue,
          }),
        };
      });
    },
    [setFilterListAtomMap, scopeKey],
  );

  /**
   * - 특정 instanceId의 상태를 교체하는 함수 (merge 없이 전체 교체)
   * - route params가 변경될 때 이전 스크린의 stale 값을 제거하기 위해 사용
   */
  const replaceFilterListAtom = useCallback(
    (newValue: Partial<typeof filterListAtomValue>) => {
      setFilterListAtomMap(prev => ({
        ...prev,
        [scopeKey]: newValue as typeof filterListAtomValue,
      }));
    },
    [setFilterListAtomMap, scopeKey],
  );

  /**
   * - filterListAtom의 search_keyword 초기화
   */
  const clearSearchKeyword = useCallback(() => {
    syncNavigationParams({ search_keyword: undefined });
    setFilterListAtom(prev => ({
      ...prev,
      search_keyword: undefined,
    }));
  }, [setFilterListAtom, syncNavigationParams]);

  /**
   * - 현재 instanceId의 filterListAtom을 초기화
   */
  const clearFilterList = useCallback(() => {
    setFilterListAtomMap(prev => ({
      ...prev,
      [scopeKey]: {},
    }));
  }, [setFilterListAtomMap, scopeKey]);

  /**
   * - 칩 필터만 초기화 (라디오 필터는 보존)
   */
  const clearChipFilters = useCallback(
    (radioQueryName?: string) => {
      setFilterListAtomMap(prev => {
        const currentValue = prev[scopeKey] ?? {};
        const preserved: Record<string, unknown> = {};
        if (currentValue.screen_name !== undefined) {
          preserved.screen_name = currentValue.screen_name;
        }
        if (radioQueryName && currentValue[radioQueryName] !== undefined) {
          preserved[radioQueryName] = currentValue[radioQueryName];
        }
        return {
          ...prev,
          [scopeKey]: preserved,
        };
      });
    },
    [setFilterListAtomMap, scopeKey],
  );

  /**
   * - 오늘출발 칩과 같이 type이 toggle인 경우 사용
   */
  const setToggleFilterChip = useCallback(
    ({
      queryName,
      queryValue,
      isSelected,
    }: {
      queryName: string;
      queryValue: string;
      isSelected: boolean;
    }) => {
      const nextValue = { [queryName]: isSelected ? undefined : queryValue };
      setFilterListAtom(prev => ({
        ...prev,
        ...nextValue,
      }));
      syncNavigationParams(nextValue);
    },
    [setFilterListAtom, syncNavigationParams],
  );

  /**
   * - 라디오 필터 적용. `screen_name` / 자기 `queryName` / `preservedQueryNames` 만 남기고
   *   나머지 필터는 제거한다.
   * - 쓰는 위치는 스코프 유무로 갈린다(`isScopedFilter` 주석 참고).
   *   - 스코프 있음(홈 탭): atom 슬롯을 보존 대상 + 새 값으로 교체. params 는 건드리지 않는다.
   *   - 스코프 없음(screens/search): 제거와 새 값을 **한 번의 setParams 로** 넘긴다.
   *     나눠 호출하면 중간 상태의 params 로 atom 이 replace 되어 불필요한 refetch 가 생긴다.
   */
  const setRadioFilter = useCallback(
    ({
      queryName,
      queryValue,
      preservedQueryNames = [],
    }: {
      queryName: string;
      queryValue: string;
      /** 함께 남길 다른 필터의 query_name 목록. */
      preservedQueryNames?: string[];
    }) => {
      const preservedKeys = new Set([
        'screen_name',
        queryName,
        ...preservedQueryNames,
      ]);

      if (isScopedFilter) {
        setFilterListAtomMap(prev => {
          const currentValue = prev[scopeKey] ?? {};
          const preserved = Object.fromEntries(
            Object.entries(currentValue).filter(([key]) =>
              preservedKeys.has(key),
            ),
          );
          return {
            ...prev,
            [scopeKey]: omitUndefined({
              ...preserved,
              [queryName]: queryValue,
            }),
          };
        });
        return;
      }

      // 제거 대상은 atom 기준으로 고른다. atom 은 params 에서 네비게이션 전용 키
      // (instanceId/next_token 등)를 걸러낸 값이라, 그것들을 실수로 지우지 않는다.
      const removedParams = Object.fromEntries(
        Object.keys(filterListAtomValue)
          .filter(key => !preservedKeys.has(key))
          .map(key => [key, undefined]),
      );

      setNavigationParams({ ...removedParams, [queryName]: queryValue });
    },
    [
      isScopedFilter,
      setFilterListAtomMap,
      scopeKey,
      filterListAtomValue,
      setNavigationParams,
    ],
  );

  const setFixedRadioFilter = useCallback(
    ({
      queryName,
      queryValue,
      isSelected,
    }: {
      queryName: string;
      queryValue: string;
      isSelected: boolean;
    }) => {
      const nextValue = { [queryName]: isSelected ? undefined : queryValue };
      setFilterListAtom(prev => ({
        ...prev,
        ...nextValue,
      }));
      syncNavigationParams(nextValue);
    },
    [setFilterListAtom, syncNavigationParams],
  );

  const setSearchKeyword = useCallback(
    (searchKeyword: string) => {
      syncNavigationParams({ search_keyword: searchKeyword });

      setFilterListAtom(prev => ({
        ...prev,
        search_keyword: searchKeyword,
      }));
    },
    [setFilterListAtom, syncNavigationParams],
  );
  return {
    clearSearchKeyword,
    clearFilterList,
    clearChipFilters,
    filterListAtomValue,
    replaceFilterListAtom,
    setRadioFilter,
    setFixedRadioFilter,
    setToggleFilterChip,
    setSearchKeyword,
    setFilterListAtom,
  };
};
